import { createPortal } from 'react-dom'
import { Users, HeartHandshake, Star, Bell } from 'lucide-react'
import { useScrollLock } from '@/hooks/useScrollLock'
import type { ChangelogEntry } from '@/lib/changelog'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  users: Users,
  hand: HeartHandshake,
  star: Star,
  bell: Bell,
}

interface Props {
  changelog: ChangelogEntry
  onDismiss: () => void
}

export function ChangelogModal({ changelog, onDismiss }: Props) {
  useScrollLock()

  return createPortal(
    <div
      data-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          zIndex: 9999,
          backgroundColor: '#FFFFFF',
          borderRadius: 20,
          padding: 32,
          width: '90vw',
          maxWidth: 400,
          maxHeight: 'calc(100dvh - env(safe-area-inset-top, 44px) - 80px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center' }}>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 500,
              color: '#8E8E93',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Nouveautes {changelog.version}
          </p>
          <h2
            style={{
              margin: '8px 0 0',
              fontSize: 22,
              fontWeight: 600,
              lineHeight: 1.2,
              color: '#000000',
              letterSpacing: '-0.01em',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {changelog.title}
          </h2>
        </div>

        {/* Items */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {changelog.items.map((item, idx) => {
            const IconComponent = ICON_MAP[item.icon]
            return (
              <div key={idx} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div
                  style={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    backgroundColor: '#F2F2F7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {IconComponent && (
                    <IconComponent size={20} style={{ color: '#8E8E93' }} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 15,
                      fontWeight: 600,
                      color: '#000000',
                      lineHeight: 1.3,
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {item.title}
                  </p>
                  <p
                    style={{
                      margin: '4px 0 0',
                      fontSize: 14,
                      fontWeight: 400,
                      color: '#8E8E93',
                      lineHeight: 1.5,
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {item.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        {/* CTA */}
        <button
          onClick={onDismiss}
          style={{
            width: '100%',
            padding: '14px 24px',
            borderRadius: 24,
            border: 'none',
            backgroundColor: '#000000',
            color: '#FFFFFF',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          C'est parti !
        </button>
      </div>
    </div>,
    document.body,
  )
}
